import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { AxiosInstance } from 'axios';

import { useLocalStorage } from '../hooks/useLocalStorage';
import { useAppDispatch, useAppSelector } from '../hooks/hooksRedux';

import { RootState } from '.';

import { AuthService } from '../services/AuthService';
import { IUser } from '../interfaces/entities';
import { ICompany } from '../services/Interfaces/IAuthService';
import { useApi } from '../services/hooks/apiHook';

import { addPersonalitySlug } from './globalSlice/languageSlice/LanguageSlice';
import { fetchStyleGuide } from './globalSlice/styleSlice';
import { OFFSHORE_FLOW_SESSION_KEY, OFFSHORE_JURISDICTION_SESSION_KEY } from '../constants/offshoreLpIntent';
import { clearTransferStepSession } from '../pages/Onboarding/CompanyTransfer/constants';

interface IAuthContext {
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: IUser;
  token?: string | null | undefined;
  setToken: Dispatch<SetStateAction<string | null | undefined>>;
  planSlug: string;
  setPlanSlug: Dispatch<SetStateAction<string>>;
  configSlug: string;
  setConfigSlug: Dispatch<SetStateAction<string>>;
  company?: ICompany;
  selectCompany: (company: ICompany) => void;
  setCompany: any;
  paymentCompany: string | null | undefined;
  setPaymentCompany: any;
  logout: () => void;
}

const AuthContext = createContext<IAuthContext>({
  isAuthenticated: false,
  isLoading: true,
  user: undefined,
  company: undefined,
  selectCompany: () => undefined,
  setCompany: {},
  paymentCompany: null,
  setPaymentCompany: () => null,
  setToken: () => null,
  token: undefined,
  planSlug: '',
  setPlanSlug: () => '',
  configSlug: '',
  setConfigSlug: () => '',
  logout: () => undefined
});

const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<IUser | undefined>();
  const [company, setCompany] = useLocalStorage<ICompany | undefined>('company', undefined);
  const [paymentCompany, setPaymentCompany] = useLocalStorage<string | null | undefined>('paymentCompany', null);
  const [token, setToken] = useLocalStorage<string | null | undefined>('access-token', undefined);
  const [planSlug, setPlanSlug] = useLocalStorage<string>('slug-plan', '');
  const [configSlug, setConfigSlug] = useLocalStorage<string>('config-plan', '');
  const [isLoading, setIsLoading] = useState(() => {
    // Se há token, assume que está carregando até o user ser carregado
    const storedToken = localStorage.getItem('access-token');
    return !!storedToken;
  });

  const { legalPersonalitySlug } = useAppSelector((state: RootState) => state.language);
  const dispatch = useAppDispatch();
  const api: AxiosInstance = useApi();

  const fetchMe = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      setIsLoading(true);
      const { user } = await AuthService.me(api, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Language': legalPersonalitySlug }
      });
      setUser(user);
      dispatch(addPersonalitySlug(user.preferred_language));
    } catch (e: any) {
      void clearTokenState();
      setUser(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [api, token, legalPersonalitySlug, dispatch]);

  const selectCompany = (company: ICompany) => {
    setCompany(company);
  };

  const lastValidatedTokenRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsLoading(false);
        lastValidatedTokenRef.current = token;
        return;
      }

      if (lastValidatedTokenRef.current === token) {
        return;
      }

      lastValidatedTokenRef.current = token;
      setIsLoading(true);
      await fetchMe();
      dispatch(fetchStyleGuide());
    };

    void validateToken();
  }, [token, fetchMe, dispatch]);

  const logout = useCallback(() => {
    setUser(undefined);
    setToken(undefined);
    setCompany(undefined);
    setPaymentCompany(null);
    setPlanSlug('');
    setConfigSlug('');
    // Drop LP / transfer session intents so the next login is not hijacked.
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(OFFSHORE_FLOW_SESSION_KEY);
      sessionStorage.removeItem(OFFSHORE_JURISDICTION_SESSION_KEY);
      clearTransferStepSession();
    }
  }, [setUser, setToken, setCompany, setPaymentCompany, setPlanSlug, setConfigSlug]);

  const values = useMemo(
    () => ({
      isAuthenticated: !!user,
      token,
      user,
      setToken,
      isLoading,
      logout,
      selectCompany,
      setCompany,
      paymentCompany,
      setPaymentCompany,
      company,
      planSlug,
      setPlanSlug,
      configSlug,
      setConfigSlug
    }),
    [token, user, isLoading, setCompany, company, planSlug, configSlug, paymentCompany]
  );

  const clearTokenState = logout;

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = useContext(AuthContext);

  return context;
};

export { AuthContext, AuthProvider, useAuth };
